import { useEffect, useMemo } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getMerchBaseUrl } from "../utils/apiBase";
import { buildQueryTokenLoginPath, isPlatformLogout } from "../utils/Helper";
import { readSsoQueryToken } from "../utils/ssoQueryToken";

const EVENT_APP_BASE = String(process.env.REACT_APP_BASE_URL || "/event").replace(
  /\/$/,
  ""
);

/** Path only. REACT_APP_BASE_URL is sometimes an absolute URL. */
function eventAppBasePath() {
  const configured = String(EVENT_APP_BASE || "/event");
  if (/^https?:\/\//i.test(configured)) {
    try {
      return new URL(configured).pathname.replace(/\/$/, "") || "";
    } catch {
      return "/event";
    }
  }
  return configured.startsWith("/") ? configured : `/${configured}`;
}

/** Merch login with platform SSO; returns user to this event URL after auth. */
function buildMerchEventLoginUrl(location) {
  const basePath = eventAppBasePath();
  const path = `${location.pathname || "/"}${location.search || ""}`;
  const returnPath = path === basePath || path.startsWith(`${basePath}/`)
    ? path
    : `${basePath}${path.startsWith("/") ? path : `/${path}`}`;
  const returnUrl = `${window.location.origin}${returnPath}`;
  const params = new URLSearchParams({
    platform: "event",
    redirect: returnUrl,
  });
  const referralId = new URLSearchParams(location.search).get("referralId");
  if (referralId) {
    params.set("referralId", referralId);
  }
  return `${getMerchBaseUrl()}/login?${params.toString()}`;
}

/**
 * Auth gate for the CRA event app.
 * - Default: requires a user session. A ?token= / ?Token= / ?authId= handoff goes to /auth; otherwise merch login.
 * - organizer: also requires userType === "organizer".
 * - guard: requires a guard session; otherwise redirect to /guard/login.
 * Public browsing stays in the nextevent app (/event/pub-access/*, etc.).
 */
function PrivateOutlet({ children, organizer, guard }) {
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const guardSession = useSelector((state) => state.guard);

  const requireGuard = Boolean(guard);
  const requireOrganizer = Boolean(organizer);
  const isGuardAuthed = Boolean(guardSession?.token);
  const isUserAuthed = Boolean(user?.token);
  const isOrganizer =
    isUserAuthed && user?.userDetails?.userType === "organizer";
  const tokenLoginPath = requireGuard
    ? ""
    : buildQueryTokenLoginPath(
        location.pathname,
        location.search,
        location.hash
      );

  const ssoPending = useMemo(
    () => Boolean(readSsoQueryToken(location.search)),
    [location.search]
  );

  useEffect(() => {
    if (requireGuard || isUserAuthed || ssoPending || isPlatformLogout()) return;
    window.location.assign(buildMerchEventLoginUrl(location));
  }, [requireGuard, isUserAuthed, ssoPending, location]);

  if (requireGuard) {
    if (!isGuardAuthed) {
      return <Navigate to="/guard/login" replace />;
    }
    return children ? children : <Outlet />;
  }

  if (!isUserAuthed) {
    return null;
  }

  if (requireOrganizer && !isOrganizer) {
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
}

export default PrivateOutlet;
